import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { OutboxStatus } from '@tipstrack/shared';
import {
  fetchIndeedStatus,
  lastRunsList,
  listJobs,
  listOutbox,
  listSyncRuns,
  mockApply,
  retryOutboxItem,
  type MockApplyInput,
} from '../api/endpoints.js';
import { errorMessage } from '../api/client.js';
import { fmtDateTime, shortId } from '../lib/format.js';

const OUTBOX_FILTERS: Array<OutboxStatus | ''> = ['', 'pending', 'sent', 'failed', 'skipped'];

const EMPTY_MOCK_FORM: MockApplyInput = {
  jobId: '',
  fullName: '',
  email: '',
  phone: '',
  resumeText: '',
};

export function SettingsPage() {
  const queryClient = useQueryClient();

  const statusQuery = useQuery({ queryKey: ['indeed-status'], queryFn: fetchIndeedStatus });
  const runsQuery = useQuery({ queryKey: ['sync-runs'], queryFn: listSyncRuns });
  const jobsQuery = useQuery({ queryKey: ['jobs'], queryFn: listJobs });

  const [outboxFilter, setOutboxFilter] = useState<OutboxStatus | ''>('');
  const outboxQuery = useQuery({
    queryKey: ['outbox', outboxFilter],
    queryFn: () => listOutbox(outboxFilter),
  });

  const [error, setError] = useState<string | null>(null);
  const [mockForm, setMockForm] = useState<MockApplyInput>(EMPTY_MOCK_FORM);
  const [mockResult, setMockResult] = useState<string | null>(null);

  const retryMutation = useMutation({
    mutationFn: (id: number) => retryOutboxItem(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['outbox'] });
      void queryClient.invalidateQueries({ queryKey: ['indeed-status'] });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const mockMutation = useMutation({
    mutationFn: (input: MockApplyInput) =>
      mockApply({
        jobId: input.jobId,
        fullName: input.fullName.trim(),
        email: input.email.trim(),
        phone: input.phone?.trim() || undefined,
        resumeText: input.resumeText?.trim() || undefined,
      }),
    onSuccess: (_data, vars) => {
      setMockResult(`Test applicant "${vars.fullName}" injected through the webhook path.`);
      setMockForm(EMPTY_MOCK_FORM);
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      void queryClient.invalidateQueries({ queryKey: ['pipeline-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['indeed-status'] });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  function onMockSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMockResult(null);
    mockMutation.mutate(mockForm);
  }

  const status = statusQuery.data;
  const statusRuns = lastRunsList(status);
  const runs = runsQuery.data ?? [];
  const outbox = outboxQuery.data ?? [];
  const jobs = jobsQuery.data ?? [];

  return (
    <div className="page">
      <header className="page__header">
        <h1>Settings</h1>
      </header>

      {error && <div className="alert alert--error">{error}</div>}

      <section className="card">
        <h2 className="card__title">Indeed integration</h2>
        {statusQuery.isLoading && <div className="spinner" aria-label="Loading status" />}
        {statusQuery.isError && (
          <div className="alert alert--error">Failed to load Indeed status.</div>
        )}
        {status && (
          <>
            <div className="kv-row">
              <span className="kv">
                <span className="kv__label">Mode</span>
                <span className={`pill ${status.mode === 'mock' ? 'sync-pending' : 'sync-active'}`}>
                  {status.mode}
                </span>
              </span>
              <span className="kv">
                <span className="kv__label">Jobs synced</span>
                <span className="kv__value">{status.jobsSynced}</span>
              </span>
              <span className="kv">
                <span className="kv__label">Pending outbox</span>
                <span className="kv__value">{status.pendingOutbox}</span>
              </span>
            </div>
            {statusRuns.length > 0 && (
              <>
                <h3 className="card__subtitle">Last runs</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Kind</th>
                      <th>Started</th>
                      <th>Finished</th>
                      <th>OK</th>
                      <th>Items</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusRuns.map((r) => (
                      <tr key={`${r.kind}-${r.id}`}>
                        <td>{r.kind}</td>
                        <td>{fmtDateTime(r.startedAt)}</td>
                        <td>{fmtDateTime(r.finishedAt)}</td>
                        <td>{r.ok == null ? '…' : r.ok ? 'yes' : 'no'}</td>
                        <td>{r.items}</td>
                        <td>{r.errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </section>

      <section className="card">
        <div className="card__header-row">
          <h2 className="card__title">Disposition outbox</h2>
          <label className="field field--inline">
            <span className="field__label">Status</span>
            <select
              value={outboxFilter}
              onChange={(e) => setOutboxFilter(e.target.value as OutboxStatus | '')}
            >
              {OUTBOX_FILTERS.map((f) => (
                <option key={f || 'all'} value={f}>
                  {f || 'all'}
                </option>
              ))}
            </select>
          </label>
        </div>
        {outboxQuery.isLoading && <div className="spinner" aria-label="Loading outbox" />}
        {outboxQuery.isError && <div className="alert alert--error">Failed to load outbox.</div>}
        {!outboxQuery.isLoading && outbox.length === 0 && (
          <p className="muted">No outbox items{outboxFilter ? ` with status "${outboxFilter}"` : ''}.</p>
        )}
        {outbox.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Disposition</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Next attempt</th>
                <th>Last error</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {outbox.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Link to={`/applications/${item.applicationId}`}>
                      {shortId(item.applicationId)}
                    </Link>
                  </td>
                  <td>{item.disposition}</td>
                  <td>
                    <span className={`pill outbox-${item.status}`}>{item.status}</span>
                  </td>
                  <td>{item.attempts}</td>
                  <td>{fmtDateTime(item.nextAttemptAt)}</td>
                  <td className="cell-error" title={item.lastError ?? undefined}>
                    {item.lastError ?? '—'}
                  </td>
                  <td>
                    {(item.status === 'failed' || item.status === 'pending') && (
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        disabled={retryMutation.isPending}
                        onClick={() => retryMutation.mutate(item.id)}
                      >
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">Recent sync runs</h2>
        {runsQuery.isLoading && <div className="spinner" aria-label="Loading sync runs" />}
        {!runsQuery.isLoading && runs.length === 0 && <p className="muted">No sync runs yet.</p>}
        {runs.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Mode</th>
                <th>Started</th>
                <th>Finished</th>
                <th>OK</th>
                <th>Items</th>
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{r.kind}</td>
                  <td>{r.mode}</td>
                  <td>{fmtDateTime(r.startedAt)}</td>
                  <td>{fmtDateTime(r.finishedAt)}</td>
                  <td>{r.ok == null ? '…' : r.ok ? 'yes' : 'no'}</td>
                  <td>{r.items}</td>
                  <td>{r.errors}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {status?.mode === 'mock' && (
        <section className="card">
          <h2 className="card__title">Inject test applicant (mock mode)</h2>
          <p className="muted">
            Builds a realistic Indeed Apply payload, signs it, and dispatches it through the real
            webhook path.
          </p>
          {mockResult && <div className="alert alert--success">{mockResult}</div>}
          <form onSubmit={onMockSubmit} className="form-grid">
            <label className="field field--full">
              <span className="field__label">Job</span>
              <select
                value={mockForm.jobId}
                onChange={(e) => setMockForm((f) => ({ ...f, jobId: e.target.value }))}
                required
              >
                <option value="">Select a job…</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title} ({j.status})
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Full name</span>
              <input
                value={mockForm.fullName}
                onChange={(e) => setMockForm((f) => ({ ...f, fullName: e.target.value }))}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Email</span>
              <input
                type="email"
                value={mockForm.email}
                onChange={(e) => setMockForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Phone (optional)</span>
              <input
                value={mockForm.phone ?? ''}
                onChange={(e) => setMockForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </label>
            <label className="field field--full">
              <span className="field__label">Resume text (optional)</span>
              <textarea
                value={mockForm.resumeText ?? ''}
                onChange={(e) => setMockForm((f) => ({ ...f, resumeText: e.target.value }))}
                rows={4}
                placeholder="Experienced SDR with 3 years of outbound…"
              />
            </label>
            <div className="modal__actions">
              <button type="submit" className="btn btn--primary" disabled={mockMutation.isPending}>
                {mockMutation.isPending ? 'Injecting…' : 'Inject test applicant'}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
